<template>
  <div class="unsubscribe-page">
    <div class="container">
      <div class="unsubscribe-content">
        <div v-if="isLoading" class="loading-state">
          <h2>Processing your unsubscribe request...</h2>
          <div class="spinner"></div>
        </div>
        
        <div v-else-if="success" class="success-state">
          <h2>Successfully Unsubscribed</h2>
          <p>You have been successfully unsubscribed.</p>
          <div class="action-buttons">
            <router-link to="/" class="btn btn-primary">
              Return to Home
            </router-link>
          </div>
        </div>
        
        <div v-else-if="error" class="error-state">
          <h2>Unable to Unsubscribe</h2>
          <p>An error occurred when trying to unsubscribe, please <router-link to="/contact" class="contact-link">contact me</router-link>.</p>
          <div class="action-buttons">
            <router-link to="/" class="btn btn-primary">
              Return to Home
            </router-link>
          </div>
        </div>
        
        <div v-else class="invalid-state">
          <h2>Invalid Unsubscribe Link</h2>
          <p>This unsubscribe link appears to be invalid or expired.</p>
          <p>Please use the unsubscribe link from the most recent email of this type. If you continue to have trouble, please <router-link to="/contact" class="contact-link">contact me</router-link>.</p>
          <div class="action-buttons">
            <router-link to="/" class="btn btn-primary">
              Return to Home
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useApi } from '@/composables/useApi';

export default {
  name: 'UnsubscribePage',
  setup() {
    const route = useRoute();
    const { account, isLoading, clearError } = useApi();
    
    const success = ref(false);
    const error = ref(null);
    
    const processUnsubscribe = async () => {
      try {
        clearError();
        
        // Get the token from the URL query parameters
        const token = route.query.token;
        
        if (!token) {
          return;
        }
        
        // Call the unsubscribe API
        await account.unsubscribe(token);
        success.value = true;
        
      } catch (err) {
        error.value = 'An unexpected error occurred while processing your unsubscribe request.';
      }
    };
    
    onMounted(() => {
      processUnsubscribe();
    });
    
    return {
      isLoading,
      success,
      error
    };
  }
};
</script>

<style scoped>
.unsubscribe-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;
  padding: 2rem 1rem;
}

.container {
  max-width: 600px;
  width: 100%;
}

.unsubscribe-content {
  background: #f5f5f5;
  border-radius: 12px;
  padding: 3rem 2rem;
  text-align: center;
  border: 1px solid #e5e5e5;
}

h2 {
  font-size: 2.5rem;
  font-weight: 300;
  margin-bottom: 1.5rem;
  color: #312d30;
  font-family: 'StayWonderful', 'Pacifico', 'Segoe Script', 'Brush Script MT', cursive;
}

p {
  font-size: 1rem;
  line-height: 1.7;
  color: #312d30;
  margin-bottom: 1rem;
  font-family: 'Quicksand', 'Segoe UI', 'Georgia', serif;
  font-weight: 500;
  letter-spacing: 0.01em;
}

.error-message {
  color: #dc2626;
  font-weight: 500;
  font-family: 'Quicksand', 'Segoe UI', 'Georgia', serif;
}

.contact-link {
  color: #7da47e;
  text-decoration: none;
  font-weight: 500;
  font-family: 'Quicksand', 'Segoe UI', 'Georgia', serif;
}

.contact-link:hover {
  color: #388E3C;
  text-decoration: underline;
}

.action-buttons {
  margin-top: 2rem;
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.btn {
  display: inline-block;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
}

.btn-primary {
  background-color: #7da47e;
  color: white;
}

.btn-primary:hover {
  background-color: #388E3C;
}

.btn-secondary {
  background-color: #888;
  color: white;
}

.btn-secondary:hover {
  background-color: #555;
}

.loading-state h2 {
  color: #312d30;
}

.success-state h2 {
  color: #312d30;
}

.error-state h2,
.invalid-state h2 {
  color: #312d30;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e5e5;
  border-top: 3px solid #7da47e;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 1rem auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 640px) {
  .unsubscribe-content {
    padding: 2rem 1.5rem;
  }
  
  .action-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .btn {
    width: 100%;
    max-width: 200px;
  }
}
</style>